// ============================================================================
// "Repeat this one." Addendum part 2B. 2026-09-03.
//
// The commonest thing an AM types is a job they have typed before: the same
// strip-and-wax at the same building with the same sub at the same rate, a month
// later. Today that is the whole form again from an empty screen, which is how
// a rate gets fat-fingered and how a record gets abandoned.
//
// ⛔ WHAT A REPEAT CARRIES, AND WHAT IT MUST NOT.
//
// CARRIES — the things that describe the WORK, and are the reason to repeat it
// at all: the building, the classification, the service lines with their scope,
// their rates and their sub.
//
// DROPS — the things that describe THIS OCCURRENCE and would be a lie on the
// next one: both dates on the project and on every line, the stage (a repeat is
// a fresh Draft), and everything downstream of money having moved.
//
// ⛔ AND IT DOES NOT COPY A CHARGEBACK LINE. A chargeback takes money back from
// a sub for a specific failure on a specific day. Carrying it into "we did that
// job again" would re-penalise somebody for an incident that has already been
// settled — silently, inside a convenience button. If the new job also needs
// one, the AM adds it deliberately, which is the only way that should ever
// happen.
//
// ⚠️ The amounts are RE-DERIVED from the copied lines rather than copied from
// the old project row. The old row's totals may include a chargeback we just
// dropped, or a hand-entered figure that no longer matches its lines — either
// way, copying them forward would carry a number whose own lines disagree with
// it. Same rule the entry screen uses: lines are the authority (TCSD-0061).
// ============================================================================
import type { ProjectServiceLineInput } from '../hooks/use-project-data';

/** The chargeback marker, as `entry-derive` and the database both spell it. */
export const CHARGEBACK_LINE_ROLE = 'chargeback';

/** Only the columns a repeat reads. Keeps this testable without a live row. */
export interface RepeatableLine {
  line_role?: string | null;
  service_code?: string | null;
  scope_text?: string | null;
  scope_qty?: number | null;
  client_rate?: number | null;
  sub_rate?: number | null;
  assigned_partner_id?: string | null;
  line_order?: number | null;
  notes?: string | null;
}

export function isChargebackLine(line: RepeatableLine): boolean {
  return (line.line_role ?? '') === CHARGEBACK_LINE_ROLE;
}

/**
 * The service lines a repeat should create.
 *
 * ⛔ Dates are dropped on EVERY line. A repeat with last month's service date on
 * it is not a new job, it is a duplicate of an old one — and `deriveDates` would
 * then hand the new project the old project's dates, which is how a record ends
 * up claiming work happened on a day nobody worked.
 */
export function repeatLines(lines: RepeatableLine[]): ProjectServiceLineInput[] {
  return lines
    .filter((l) => !isChargebackLine(l))
    .map((l, i) => ({
      line_role: 'service',
      service_code: l.service_code ?? null,
      scope_text: l.scope_text ?? null,
      scope_qty: l.scope_qty ?? null,
      client_rate: l.client_rate ?? null,
      sub_rate: l.sub_rate ?? null,
      assigned_partner_id: l.assigned_partner_id ?? null,
      // ⛔ Both dates deliberately null. See the header.
      service_date_planned: null,
      service_date_actual: null,
      line_order: i + 1,
      notes: l.notes ?? null,
    }));
}

/**
 * Sum a rate across the repeated lines, or NULL when no line carries one.
 *
 * ⛔ NULL IS NOT ZERO AND IS NEVER COALESCED (TCSD-0062). "No line has a client
 * rate" and "every line is free" are different facts, and the advance gate
 * reads the difference: a NULL bill amount blocks, a zero one does not.
 */
export function sumRate(
  lines: ProjectServiceLineInput[],
  key: 'client_rate' | 'sub_rate',
): number | null {
  const present = lines.map((l) => l[key]).filter((v): v is number => typeof v === 'number');
  if (present.length === 0) return null;
  return present.reduce((a, b) => a + b, 0);
}

export interface RepeatSource {
  facility_id: string | null;
  account_id: string | null;
  event_class: string | null;
  reason_code: string | null;
  reason_note: string | null;
  notes: string | null;
}

export interface RepeatDraft {
  facility_id: string;
  account_id: string;
  event_class: string;
  reason_code: string | null;
  reason_note: string | null;
  start_date: null;
  completion_date: null;
  due_date: null;
  bill_amount: number | null;
  pay_amount: number | null;
  special_pay: false;
  awaiting_schedule: false;
  notes: string | null;
}

/**
 * What "repeat this one" should insert, given the record being repeated.
 *
 * ⛔ Returns null when the source cannot be repeated at all — a project with no
 * facility, or one whose every line was a chargeback. Refusing plainly beats
 * creating an empty Draft the AM then has to work out how to delete.
 */
export function buildRepeat(
  source: RepeatSource,
  lines: RepeatableLine[],
): { project: RepeatDraft; lines: ProjectServiceLineInput[] } | null {
  if (!source.facility_id || !source.account_id) return null;
  const next = repeatLines(lines);
  if (next.length === 0) return null;

  return {
    project: {
      facility_id: source.facility_id,
      account_id: source.account_id,
      // The classification travels — it is a fact about the KIND of work, and
      // repeating a Coverage record as ordinary project work would misfile it.
      event_class: source.event_class ?? 'Project',
      reason_code: source.reason_code,
      reason_note: source.reason_note,
      start_date: null,
      completion_date: null,
      due_date: null,
      bill_amount: sumRate(next, 'client_rate'),
      pay_amount: sumRate(next, 'sub_rate'),
      // ⛔ Never carried. `special_pay` is a MINT GATE (TCSD-0069) — repeating it
      // would arm a payment on a record nobody has looked at yet.
      special_pay: false,
      awaiting_schedule: false,
      notes: source.notes,
    },
    lines: next,
  };
}
